import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Crown, Loader2, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FoundingMembership } from "@/hooks/useFoundingMembership";

interface Props {
  membership: FoundingMembership | null;
  isFoundingMember: boolean;
  hasBadge: boolean;
  onCancelled?: () => void;
}

export function FoundingSubscriptionCard({
  membership,
  isFoundingMember,
  hasBadge,
  onCancelled,
}: Props) {
  const { toast } = useToast();
  const [cancelling, setCancelling] = useState(false);

  if (!membership || (!isFoundingMember && !hasBadge)) return null;

  const validUntil = membership.current_period_end
    ? new Date(membership.current_period_end).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const handleCancel = async () => {
    if (!confirm("Cancel your Founding Membership auto-renewal? Your badge stays for life.")) return;
    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-founding-subscription", {
        body: {},
      });
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || "Cancellation failed");
      toast({
        title: "Auto-renewal cancelled",
        description:
          "You'll keep access until the current period ends. Your Founding Member badge stays forever.",
      });
      onCancelled?.();
    } catch (err) {
      toast({
        title: "Could not cancel",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  const isCancelled = membership.status === "cancelled" || !!membership.cancelled_at;

  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none border-t-primary/60">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-accent/15 border border-accent/40 flex items-center justify-center">
          <Crown className="w-5 h-5 text-accent" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-foreground">Founding Member</h4>
          <span className="text-xs px-2 py-0.5 rounded-lg bg-accent/15 text-accent capitalize">
            {isCancelled ? "auto-renew off" : membership.status}
          </span>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        {membership.founding_joined_at && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Crown className="w-4 h-4 text-accent" />
            <span>
              Founding Member since{" "}
              <span className="text-foreground font-medium">
                {new Date(membership.founding_joined_at).toLocaleDateString("en-IN", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </span>
          </div>
        )}
        {validUntil && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>
              {isCancelled ? "Access until" : "Next renewal"} {validUntil}
            </span>
          </div>
        )}
      </div>
      {isFoundingMember && !isCancelled && (
        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={cancelling}
            className="text-muted-foreground"
          >
            {cancelling ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : null}
            Cancel auto-renewal
          </Button>
        </div>
      )}
      {hasBadge && !isFoundingMember && (
        <p className="mt-3 text-xs text-muted-foreground">
          Your Founding Member badge is permanent. Thank you for backing the work early.
        </p>
      )}
    </Card>
  );
}